import { filterKnownPermissionKeys, type PermissionKey } from '@pcrm/shared/access';
import { companyIconKeys } from '@pcrm/shared/icons';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, apiGet, apiPost } from '../../lib/api';

/**
 * Firma pe care lucrează sesiunea. `null` e starea normală imediat după login.
 *
 * Vine din același răspuns ca operatorul, nu dintr-o cerere separată: shell-ul le afișează
 * pe amândouă la fiecare încărcare de pagină. Vezi `CurrentUserResponse` din `apps/api`.
 */
const activeCompanySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** Denumirea de lucru. Shell-ul o preferă; documentele folosesc `name`. */
  shortName: z.string().nullable(),
  icon: z.enum(companyIconKeys),
});

export type ActiveCompany = z.infer<typeof activeCompanySchema>;

const sessionSchema = z.object({
  user: z.object({
    // UUIDv7. Validat ca UUID, nu ca string liber: dacă serverul ar schimba forma
    // cheilor, vrem să pice aici, nu într-un `<Link>` care duce în gol.
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    username: z.string(),
    roles: z.array(z.object({ slug: z.string(), name: z.string() })),
    // Chei brute, filtrate mai jos prin registrul din `@pcrm/shared/access`. Un server
    // mai nou decât acest build poate trimite chei pe care UI-ul nu le cunoaște; nu e o
    // eroare, doar o funcționalitate pe care versiunea asta n-o afișează oricum.
    permissions: z.array(z.string()),
  }),
  activeCompany: activeCompanySchema.nullable(),
});

export interface SessionUser {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly username: string;
  readonly roles: ReadonlyArray<{ slug: string; name: string }>;
  readonly permissions: readonly PermissionKey[];
}

/**
 * Tot ce ține de sesiunea curentă: cine e logat și pe ce firmă lucrează.
 *
 * Un singur obiect, nu două query-uri: firma activă se schimbă odată cu sesiunea (login,
 * delogare, alt operator pe același calculator), iar două cache-uri separate ar putea
 * rămâne desincronizate exact în clipa aia.
 */
export interface Session {
  readonly user: SessionUser;
  readonly activeCompany: ActiveCompany | null;
}

export const SESSION_QUERY_KEY = ['session'] as const;

/**
 * Cine e logat. `null` înseamnă „nimeni", nu „eroare".
 *
 * Cookie-ul e `HttpOnly`, deci pagina **nu poate** să se uite la el ca să afle dacă
 * există o sesiune. Singura sursă de adevăr e serverul, iar 401 e răspunsul normal
 * pentru un vizitator nelogat — de aceea se transformă în `null` în loc să propage o
 * eroare care ar aprinde stări de eșec prin tot UI-ul.
 */
async function fetchSession(): Promise<Session | null> {
  try {
    const { user, activeCompany } = await apiGet('/auth/me', sessionSchema);
    return {
      user: { ...user, permissions: filterKnownPermissionKeys(user.permissions) },
      activeCompany,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 401) {
      return null;
    }
    throw error;
  }
}

export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    // Fără reîncercare: un 401 e un răspuns, nu o eroare de rețea de retestat.
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * Cât de des se prelungește sesiunea cât timp fila e deschisă și vizibilă.
 *
 * Fereastra de inactivitate e `SESSION_IDLE_MINUTES` (implicit 8 ore) și se măsoară de la
 * `crm_sessions.last_seen_at`, care se rescrie **doar când vine o cerere**. O filă lăsată
 * deschisă peste noapte nu trimite nimic, deci sesiunea moare deși aplicația e pe ecran —
 * exact ce s-a raportat în #48.
 *
 * Cinci minute e mult sub fereastră și e ieftin: serverul rescrie `last_seen_at` cel mult
 * o dată pe minut, deci un ping e un `SELECT` cu `JOIN` plus, uneori, un `UPDATE`.
 */
export const SESSION_KEEP_ALIVE_MS = 5 * 60_000;

/**
 * Atinge sesiunea pe server și spune dacă mai trăiește.
 *
 * **Nu scrie în cache-ul de query-uri**, intenționat. `useCurrentUser` folosește
 * `useSuspenseQuery` și aruncă pe `null`; dacă ping-ul ar pune `null` în cache, ecranul ar
 * arunca la următoarea randare, înainte ca redirectarea să apuce să se întâmple. Aici
 * răspunsul e un verdict, iar cine îl cere decide ce face cu el.
 */
export async function pingSession(): Promise<boolean> {
  return (await fetchSession()) !== null;
}

export function login(credentials: { username: string; password: string }): Promise<Session> {
  return apiPost('/auth/login', credentials, sessionSchema).then(({ user, activeCompany }) => ({
    user: { ...user, permissions: filterKnownPermissionKeys(user.permissions) },
    activeCompany,
  }));
}

export function logout(): Promise<{ ok: true }> {
  return apiPost('/auth/logout', {}, z.object({ ok: z.literal(true) }));
}

/**
 * Golește tot ce s-a citit sub sesiunea veche.
 *
 * `setQueryData` singur ar lăsa în cache listele încărcate de operatorul anterior, iar
 * următorul care se loghează pe același calculator le-ar vedea o clipă înainte de
 * refetch. Se cheamă la delogare **și** la autentificare.
 */
export function resetSessionCache(queryClient: QueryClient, session: Session | null): void {
  queryClient.clear();
  queryClient.setQueryData(SESSION_QUERY_KEY, session);
}
